import { AccommodationPricelistModel } from '@common/modelDefinition';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);

//always look to this and next year in generating repeat copies of pricelist
// const repeatRulesEnum = {
//     noRepeat: 'noRepeat',
//     weeklyOnDays: 'weeklyOnDays',
//     monthly: 'monthly',
//     yearly: 'yearly',
// };

function memoizeLast<F extends (...args: any[]) => any>(fn: F): F {
    let lastArgs: any[] | null = null;
    let lastResult: any;

    return function (...args: any[]) {
        if (lastArgs && lastArgs.length === args.length && lastArgs.every((val, i) => val === args[i])) {
            return lastResult;
        }
        lastArgs = args;
        lastResult = fn(...args);
        return lastResult;
    } as F;
}

const getDaysArray = memoizeLast(function getDaysArray(
    startTimestamp: number,
    endTimestamp: number,
    forceFullISODayMatch = false
): string[] {
    let arr: string[] = [];

    const start = dayjs(startTimestamp);
    const end = dayjs(endTimestamp);

    if (forceFullISODayMatch) {
        let current = start;
        const endISOString = end.format('YYYY-MM-DD');

        while (current.format('YYYY-MM-DD') <= endISOString) {
            arr.push(current.format('YYYY-MM-DD'));
            current = current.add(1, 'day');
        }
    } else {
        for (let dt = start; dt.isSameOrBefore(end, 'day'); dt = dt.add(1, 'day')) {
            arr.push(dt.format('YYYY-MM-DD'));
        }
    }

    return arr;
});


const getOverlappingPriceListsOnStack = (priceLists: AccommodationPricelistModel[]) => {
    let overlapps: any[] = [];
    let baselines = priceLists?.filter((pl) => pl.parentId === null);
    for (const baseline of baselines) {
        let plist = priceLists?.filter((pl) => pl.parentId === baseline.id);

        let weights = [...new Set(plist.map((pl) => pl.weight))];

        for (const w of weights) {
            let plistWithSameWeight = plist?.filter((p) => p.weight === w);

            let plistWithSameWeightAndDays = plistWithSameWeight.map((p) => ({
                ...p,
                days: getDaysArray(p.validFrom, p.validTo),
            }));
            const allDays = plistWithSameWeightAndDays.flatMap((p) => p.days);

            const conflictingDatesGrouped = allDays.reduce((acc, day) => {
                if (!acc[day]) acc[day] = [];
                acc[day].push(day);
                return acc;
            }, {} as Record<string, string[]>);

            const conflictingDates = Object.values(conflictingDatesGrouped).filter((arr) => arr.length > 1);

            const conflictingDatesUnique = conflictingDates.map((arr) => arr[0]);

            let overlappingIds: number[] = [];

            if (conflictingDatesUnique.length > 0) {
                for (const p of plistWithSameWeightAndDays) {
                    const _p: any = { ...p };
                    const intersects = p.days.filter((day) => conflictingDatesUnique.includes(day));

                    if (intersects.length > 0) {
                        if (_p?.id && !overlappingIds.includes(_p?.id)) {
                            overlappingIds.push(_p.id);
                            _p.intersects = intersects;
                            overlapps.push(_p);
                        }
                    }
                }
            }
        }
    }
    return overlapps;
};

const getNewRepeats = (
    pl: AccommodationPricelistModel,
    startFromYear = new Date().getFullYear(),
    timelineYearsCount = 1
) => {
    const priceListSpans: any[] = [];

    if (pl.weight === 0) return pl;
    if (!pl.repeatRules) return priceListSpans;

    const repeatRules = JSON.parse(pl.repeatRules);
    if (repeatRules.noRepeat) {
        priceListSpans.push(pl);
        return priceListSpans;
    }

    const fromBorder = pl.infinite ? dayjs(`${startFromYear}-01-01`) : dayjs(pl.validFrom);
    const toBorder = pl.infinite ? dayjs(`${startFromYear + timelineYearsCount}-01-01`) : dayjs(pl.validTo);

    let datePointer = fromBorder;
    const keys = Object.keys(repeatRules);

    const clonePL = (obj: any) => JSON.parse(JSON.stringify(obj));

    if (keys.includes('weeklyOnDays')) {
        let latentTempValue: any = null;

        while (datePointer.isSameOrBefore(toBorder, 'day')) {
            const temp = clonePL(pl);
            let merged = false;

            if (repeatRules.weeklyOnDays.includes(datePointer.isoWeekday())) {
                temp.validFrom = datePointer.startOf('day').valueOf();
                temp.validTo = datePointer.endOf('day').valueOf();

                if (latentTempValue) {
                    if (latentTempValue.validTo + 1000 > temp.validFrom) {
                        latentTempValue.validTo = temp.validTo;
                        merged = true;
                    } else {
                        priceListSpans.push(latentTempValue);
                    }
                }

                if (!merged) latentTempValue = temp;
            }

            datePointer = datePointer.add(1, 'day');
        }

        if (latentTempValue) {
            priceListSpans.push(latentTempValue);
        }
    } else if (keys.includes('monthly')) {
        let latentTempValue: any = null;

        while (datePointer.isSameOrBefore(toBorder, 'day')) {
            const temp = clonePL(pl);
            let merged = false;

            if (repeatRules.monthly.includes(datePointer.date())) {
                temp.validFrom = datePointer.startOf('day').valueOf();
                temp.validTo = datePointer.endOf('day').valueOf();

                if (latentTempValue) {
                    if (latentTempValue.validTo + 1000 > temp.validFrom) {
                        latentTempValue.validTo = temp.validTo;
                        merged = true;
                    } else {
                        priceListSpans.push(latentTempValue);
                    }
                }

                if (!merged) latentTempValue = temp;
            }

            datePointer = datePointer.add(1, 'day');
        }

        if (latentTempValue) {
            priceListSpans.push(latentTempValue);
        }
    } else if (keys.includes('yearly')) {
        let startDayPointer = dayjs(repeatRules.yearly.from);
        let endDayPointer = dayjs(repeatRules.yearly.to);

        while (startDayPointer.isSameOrBefore(toBorder, 'day')) {
            const temp = clonePL(pl);
            temp.validFrom = startDayPointer.startOf('day').valueOf();
            temp.validTo = endDayPointer.endOf('day').valueOf();
            priceListSpans.push(temp);

            startDayPointer = startDayPointer.add(1, 'year');
            endDayPointer = endDayPointer.add(1, 'year');
        }
    }

    return priceListSpans;
};

export { getOverlappingPriceListsOnStack };

const ParsedAccommodationPriceLists = (
    accommodationPriceLists: AccommodationPricelistModel[],
    startFromYear: number,
    timelineYearsCount: number
) => {
    const result = accommodationPriceLists?.map(pl =>
        getNewRepeats(pl, startFromYear, timelineYearsCount)
    ) || [];

    return result.flat();
};

export default ParsedAccommodationPriceLists;
